import React, { useMemo, useState } from 'react';
import { formatGrade } from '@utils/gradeUtils.js';
import { CLIMBING_STYLES } from '@utils/constants.js';
import { generateFractionalGradeTable, findAverageVGrade } from '@utils/boulderGrades.js';
import { formatAscentDate } from '@utils/dateUtils.js';
import { ChevronUp, ChevronDown } from 'lucide-react';

const RouteList = ({ data, gradeSystem }) => {
  const [sortColumn, setSortColumn] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');

  if (!data) return null;

  const isBoulder = data.routes[0]?.["Route Gear Style"] === "Boulder";

  // Generate grade table once for the component
  const gradeTable = useMemo(() => generateFractionalGradeTable(), []);

  // Function to format V-grade decimal to human-readable form
  const formatVGradeDecimal = (decimal) => {
    if (decimal === null || decimal === undefined) return '';

    // Find the closest V-grade representation
    const wholeNumber = Math.floor(decimal);
    const fraction = decimal - wholeNumber;

    if (fraction < 0.25) return `V${wholeNumber}`;
    if (fraction < 0.75) return `V${wholeNumber}/V${wholeNumber + 1}`;
    return `V${wholeNumber + 1}`;
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('desc');
    }
  };

  const SortHeader = ({ column, children }) => (
    <th
      scope="col"
      className="px-6 py-3 cursor-pointer hover:bg-gray-100 select-none"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-1">
        {children}
        {sortColumn === column && (
          sortDirection === 'asc' ?
            <ChevronUp className="h-4 w-4" /> :
            <ChevronDown className="h-4 w-4" />
        )}
      </div>
    </th>
  );

  // Sort routes based on current sort state
  const sortedRoutes = useMemo(() => {
    const routes = [...data.routes];

    // Pre-compute vgrade for boulder routes if needed
    const routesWithGrade = routes.map(route => ({
      ...route,
      _vgrade: isBoulder ? findAverageVGrade(route['Route Grade'], gradeTable) : null
    }));

    routesWithGrade.sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case 'route':
          comparison = (a['Route Name'] || '').localeCompare(b['Route Name'] || '');
          break;
        case 'crag':
          comparison = (a['Crag Name'] || '').localeCompare(b['Crag Name'] || '');
          break;
        case 'date':
          comparison = new Date(a['Ascent Date']) - new Date(b['Ascent Date']);
          break;
        case 'type':
          comparison = (a['Ascent Type'] || '').localeCompare(b['Ascent Type'] || '');
          break;
        case 'grade':
          if (isBoulder) {
            comparison = (a._vgrade ?? -1) - (b._vgrade ?? -1);
          } else {
            comparison = (a.ewbanks ?? -1) - (b.ewbanks ?? -1);
          }
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return routesWithGrade;
  }, [data.routes, sortColumn, sortDirection, isBoulder, gradeTable]);

  return (
    <div className="relative overflow-x-auto shadow-md sm:rounded-lg" style={{ maxHeight: '60vh' }}>
      <table className="w-full text-sm text-left text-gray-500">
        <thead className="text-xs text-gray-700 uppercase bg-gray-50 sticky top-0">
          <tr>
            <SortHeader column="route">Route</SortHeader>
            <SortHeader column="crag">Crag</SortHeader>
            <SortHeader column="date">Date</SortHeader>
            <SortHeader column="type">Type</SortHeader>
            <SortHeader column="grade">Grade</SortHeader>
          </tr>
        </thead>
        <tbody className="overflow-y-auto">
          {sortedRoutes.map((route, index) => (
            <tr key={`${route['Route ID']}-${index}`} className="bg-white border-b hover:bg-gray-50">
              <th scope="row" className="px-6 py-4 font-medium text-gray-900">
                {route['Route Name']}
              </th>
              <td className="px-6 py-4">{route['Crag Name']}</td>
              <td className="px-6 py-4">
                {formatAscentDate(route['Ascent Date'])}
              </td>
              <td className="px-6 py-4">{route['Ascent Type']}</td>
              <td className="px-6 py-4">
                {isBoulder ?
                  formatVGradeDecimal(route._vgrade) :
                  gradeSystem === 'ewbank' ?
                    Math.round(route.ewbanks).toString() :
                    formatGrade(
                      route.ewbanks,
                      gradeSystem,
                      route["Route Gear Style"]
                    )
                }
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default RouteList;